import { type ProspectTaskDto, useSalesGetProspect, useSalesGetTask } from "@crowdcoursing/api/openapi";
import { skipToken } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { useSalesBubbleId } from "./useSalesBubbleId";
import { useToast } from "./useToast";

function asProspectTask(data: unknown): ProspectTaskDto | undefined {
  return data && typeof data === "object" ? (data as ProspectTaskDto) : undefined;
}

export const useTaskDetails = (taskId: string) => {
  const toast = useToast();
  const salesBubbleId = useSalesBubbleId();
  const [prospectId, setProspectId] = useState<string | undefined>(undefined);

  // First, fetch the task details
  const {
    data: taskData,
    isLoading: isTaskLoading,
    error: taskError,
  } = useSalesGetTask<ProspectTaskDto | undefined>(
    salesBubbleId && taskId ? { pathParams: { bubbleId: salesBubbleId, taskId } } : skipToken,
    {
      select: asProspectTask,
    },
  );

  // Extract the prospectId from the task data when it's available
  useEffect(() => {
    if (taskData?.prospectId) {
      setProspectId(taskData.prospectId);
    }
  }, [taskData]);

  // Then, fetch the prospect details using the prospectId
  const {
    data: prospectData,
    isLoading: isProspectLoading,
    error: prospectError,
  } = useSalesGetProspect(
    salesBubbleId && prospectId ? { pathParams: { bubbleId: salesBubbleId, id: prospectId } } : skipToken,
  );

  const isLoading = isTaskLoading || isProspectLoading;
  const error = taskError || prospectError;

  // If we have the prospect data, use it. Otherwise, if we have the task data with prospectName,
  // create a minimal prospect object with the name from the task.
  const prospect =
    prospectData ||
    (taskData?.prospectName && taskData.prospectId
      ? {
          id: taskData.prospectId,
          name: taskData.prospectName,
          // Add minimal required fields to avoid errors in components expecting a full prospect
          stage: 0,
          type: "individual",
        }
      : null);

  // Show error toast if there's an error
  useEffect(() => {
    if (error) {
      toast.error("Failed to load task details");
    }
  }, [error, toast]);

  return {
    error,
    isLoading,
    prospect,
    task: taskData,
  };
};
